import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { getStoredUser } from "../lib/auth";
import { useLanguage } from "../lib/language";

function AdminTrainersPage() {
  const user = getStoredUser();
  const { language } = useLanguage();
  const isArabic = language === "ar";

  const copy = isArabic
    ? {
        eyebrow: "إدارة المدربين",
        title: "إدارة المدربين",
        subtitle: "راجع حالة كل مدرب واعتماد حسابه ونشاطه والكورسات والتسليمات التابعة له.",
        search: "ابحث عن مدرب",
        searchPlaceholder: "الاسم أو البريد أو المحافظة",
        loading: "جار تحميل بيانات المدربين...",
        empty: "لا يوجد مدربون حاليًا.",
        selectHint: "اختر مدربًا من القائمة لعرض التفاصيل.",
        courses: "الكورسات",
        students: "الطلبة",
        submissions: "التسليمات",
        assessments: "التقييمات",
        average: "متوسط الدرجات",
        recent: "أحدث التسليمات",
        courseList: "كورسات المدرب",
        trainerInfo: "بيانات المدرب",
        phone: "الهاتف",
        governorate: "المحافظة",
        education: "المرحلة",
        institution: "الجهة",
        approval: "الاعتماد",
        pending: "قيد المراجعة",
        approved: "معتمد",
        rejected: "مرفوض",
        approve: "اعتماد",
        reject: "رفض",
        statusUpdated: "تم تحديث حالة المدرب",
        noCourses: "لا توجد كورسات لهذا المدرب بعد.",
        noRecent: "لا توجد تسليمات حديثة لهذا المدرب.",
        course: "الكورس",
        assessment: "التقييم",
        student: "الطالب",
        score: "الدرجة",
        trainersCount: "عدد المدربين"
      }
    : {
        eyebrow: "Trainer Management",
        title: "Trainer Management",
        subtitle: "Review each trainer account, approval status, activity level, courses, and recent submissions.",
        search: "Search trainers",
        searchPlaceholder: "Name, email, or governorate",
        loading: "Loading trainers...",
        empty: "No trainers found yet.",
        selectHint: "Select a trainer from the list to view details.",
        courses: "Courses",
        students: "Students",
        submissions: "Submissions",
        assessments: "Assessments",
        average: "Average score",
        recent: "Recent submissions",
        courseList: "Trainer courses",
        trainerInfo: "Trainer info",
        phone: "Phone",
        governorate: "Governorate",
        education: "Stage",
        institution: "Institution",
        approval: "Approval",
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
        approve: "Approve",
        reject: "Reject",
        statusUpdated: "Trainer status updated",
        noCourses: "This trainer has no courses yet.",
        noRecent: "There are no recent submissions for this trainer.",
        course: "Course",
        assessment: "Assessment",
        student: "Student",
        score: "Score",
        trainersCount: "Trainers"
      };

  const [trainers, setTrainers] = useState([]);
  const [selectedTrainerId, setSelectedTrainerId] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [search, setSearch] = useState("");
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");

  const approvalLabel = (status) => {
    if (status === "approved") {
      return copy.approved;
    }
    if (status === "rejected") {
      return copy.rejected;
    }
    return copy.pending;
  };

  const fetchTrainers = async () => {
    if (!user?.id) {
      return [];
    }
    setIsLoadingList(true);
    try {
      const response = await api.get("/admin/trainers", { params: { admin_id: user.id } });
      const nextTrainers = response.data?.trainers || [];
      setTrainers(nextTrainers);
      setSelectedTrainerId((current) => current || nextTrainers[0]?.id || null);
      return nextTrainers;
    } catch (requestError) {
      setError(requestError.response?.data?.error || copy.loading);
      return [];
    } finally {
      setIsLoadingList(false);
    }
  };

  const fetchTrainerDetails = async (trainerId) => {
    if (!user?.id || !trainerId) {
      setSelectedTrainer(null);
      return;
    }

    setIsLoadingDetail(true);
    try {
      const response = await api.get(`/admin/trainers/${trainerId}`, {
        params: { admin_id: user.id }
      });
      setSelectedTrainer(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || copy.loading);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchTrainers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    fetchTrainerDetails(selectedTrainerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrainerId, user?.id]);

  const filteredTrainers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return trainers.filter((trainer) => {
      if (!term) {
        return true;
      }
      return [trainer.full_name, trainer.email, trainer.governorate, trainer.approval_status]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [search, trainers]);

  const summary = useMemo(() => {
    return filteredTrainers.reduce(
      (accumulator, trainer) => ({
        trainers: accumulator.trainers + 1,
        courses: accumulator.courses + Number(trainer.courses_count || 0),
        students: accumulator.students + Number(trainer.students_count || 0),
        submissions: accumulator.submissions + Number(trainer.submissions_count || 0)
      }),
      { trainers: 0, courses: 0, students: 0, submissions: 0 }
    );
  }, [filteredTrainers]);

  const updateApprovalStatus = async (approvalStatus) => {
    if (!selectedTrainerId || !user?.id) {
      return;
    }

    try {
      setIsUpdatingStatus(true);
      await api.patch(`/admin/trainers/${selectedTrainerId}/approval`, {
        admin_id: user.id,
        approval_status: approvalStatus
      });
      setFeedback(copy.statusUpdated);
      await fetchTrainers();
      await fetchTrainerDetails(selectedTrainerId);
    } catch (requestError) {
      setError(requestError.response?.data?.error || copy.loading);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <section className="workspace-page admin-page">
      {feedback && <div className="feedback-banner feedback-banner--success">{feedback}</div>}
      {error && <div className="feedback-banner feedback-banner--error">{error}</div>}

      <div className="hero-panel">
        <div>
          <p className="section-eyebrow">{copy.eyebrow}</p>
          <h2 className="section-title">{copy.title}</h2>
          <p className="section-subtitle">{copy.subtitle}</p>
        </div>
      </div>

      <section className="metric-grid admin-summary-grid admin-summary-grid--compact">
        <article className="metric-card admin-stat-card">
          <span>{copy.trainersCount}</span>
          <strong>{summary.trainers}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.courses}</span>
          <strong>{summary.courses}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.students}</span>
          <strong>{summary.students}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.submissions}</span>
          <strong>{summary.submissions}</strong>
        </article>
      </section>

      <div className="admin-management-layout">
        <div className="panel-card admin-management-list">
          <div className="field-stack">
            <label className="field-label">{copy.search}</label>
            <input
              className="text-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={copy.searchPlaceholder}
            />
          </div>

          {isLoadingList ? (
            <div className="state-panel state-panel--loading"><p>{copy.loading}</p></div>
          ) : !filteredTrainers.length ? (
            <div className="state-panel state-panel--empty"><p>{copy.empty}</p></div>
          ) : (
            <div className="admin-record-stack">
              {filteredTrainers.map((trainer) => (
                <button
                  key={trainer.id}
                  type="button"
                  className={selectedTrainerId === trainer.id ? "admin-record-card admin-record-card--active" : "admin-record-card"}
                  onClick={() => setSelectedTrainerId(trainer.id)}
                >
                  <div className="admin-record-card__header">
                    <strong>{trainer.full_name}</strong>
                    <span className="status-pill">{trainer.courses_count || 0} {copy.courses}</span>
                  </div>
                  <p className="helper-text">{trainer.email}</p>
                  <div className="admin-record-card__meta">
                    <span>{copy.approval}: {approvalLabel(trainer.approval_status)}</span>
                    <span>{copy.students}: {trainer.students_count || 0}</span>
                    <span>{copy.submissions}: {trainer.submissions_count || 0}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="panel-card admin-management-detail">
          {!selectedTrainerId ? (
            <div className="state-panel state-panel--empty"><p>{copy.selectHint}</p></div>
          ) : isLoadingDetail || !selectedTrainer ? (
            <div className="state-panel state-panel--loading"><p>{copy.loading}</p></div>
          ) : (
            <div className="stack-lg">
              <div className="card-title-row">
                <div>
                  <h3>{selectedTrainer.trainer.full_name}</h3>
                  <p className="helper-text">{selectedTrainer.trainer.email}</p>
                </div>
                <div className="admin-user-actions">
                  <span className="status-pill">{approvalLabel(selectedTrainer.trainer.approval_status)}</span>
                  <span className="status-pill">{selectedTrainer.summary.courses_count || 0} {copy.courses}</span>
                </div>
              </div>

              <div className="two-column-grid">
                <article className="admin-info-card">
                  <h4>{copy.trainerInfo}</h4>
                  <div className="stack-sm">
                    <span>{copy.phone}: {selectedTrainer.trainer.phone_number || "-"}</span>
                    <span>{copy.governorate}: {selectedTrainer.trainer.governorate || "-"}</span>
                    <span>{copy.education}: {selectedTrainer.trainer.education_level || "-"}</span>
                    <span>{copy.institution}: {selectedTrainer.trainer.institution_name || "-"}</span>
                    <span>{copy.approval}: {approvalLabel(selectedTrainer.trainer.approval_status)}</span>
                  </div>
                </article>

                <article className="admin-info-card">
                  <h4>{isArabic ? "ملخص النشاط" : "Activity summary"}</h4>
                  <div className="stack-sm">
                    <span>{copy.students}: {selectedTrainer.summary.students_count || 0}</span>
                    <span>{copy.submissions}: {selectedTrainer.summary.submissions_count || 0}</span>
                    <span>{copy.courses}: {selectedTrainer.summary.courses_count || 0}</span>
                  </div>
                  <div className="admin-user-actions">
                    <button className="ghost-button" type="button" onClick={() => updateApprovalStatus("approved")} disabled={isUpdatingStatus}>
                      {copy.approve}
                    </button>
                    <button className="danger-button" type="button" onClick={() => updateApprovalStatus("rejected")} disabled={isUpdatingStatus}>
                      {copy.reject}
                    </button>
                  </div>
                </article>
              </div>

              <div>
                <h4>{copy.courseList}</h4>
                {!selectedTrainer.courses.length ? (
                  <div className="state-panel state-panel--empty"><p>{copy.noCourses}</p></div>
                ) : (
                  <div className="table-shell">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{copy.course}</th>
                          <th>{copy.assessments}</th>
                          <th>{copy.students}</th>
                          <th>{copy.submissions}</th>
                          <th>{copy.average}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTrainer.courses.map((course) => (
                          <tr key={course.id}>
                            <td>{course.title}</td>
                            <td>{course.assessments_count || 0}</td>
                            <td>{course.students_count || 0}</td>
                            <td>{course.submissions_count || 0}</td>
                            <td>{course.average_score ?? "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h4>{copy.recent}</h4>
                {!selectedTrainer.recent_submissions.length ? (
                  <div className="state-panel state-panel--empty"><p>{copy.noRecent}</p></div>
                ) : (
                  <div className="table-shell">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{copy.student}</th>
                          <th>{copy.course}</th>
                          <th>{copy.assessment}</th>
                          <th>{copy.score}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTrainer.recent_submissions.map((submission) => (
                          <tr key={submission.submission_id}>
                            <td>{submission.student_name}</td>
                            <td>{submission.course_title}</td>
                            <td>{submission.assessment_title}</td>
                            <td>{submission.score ?? "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default AdminTrainersPage;
